import React, { useState, useEffect, useMemo, useCallback } from 'react';
import supabase from '../../supabase';
import InvoicePDF from './InvoicePDF';
import { PDFViewer } from '@react-pdf/renderer';
import './InvoiceForm.css';
import ClientSelection from './ClientSelection';
import AddClientPopup from './AddClientPopup';
import InvoiceDetails from './InvoiceDetails';
import InvoiceOptions from './InvoiceOptions';
import QRCode from 'qrcode';
import TermsConditions from './TermsConditions';
import ItemsSection from './ItemsSection';
import InvoiceSummary from './InvoiceSummary';
import RecurringInvoiceSection from './InvoiceRecur';
import { useToast } from './Toast';
import { useSubscription } from '../../Context/SubscriptionContext';
import { AlertCircle } from 'lucide-react';

const InvoiceForm = React.memo(() => {
  const [user, setUser] = useState(null);
  const [fromInfo, setFromInfo] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [newClient, setNewClient] = useState({ name: '', email: '', company: '', gst: '', address: '' });
  const [showAddClientPopup, setShowAddClientPopup] = useState(false);
  const [invoiceInfo, setInvoiceInfo] = useState({
    number: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    notes: ''
  });
  const [items, setItems] = useState([{ product_id: '', product_name: '', unit_price: 0, quantity: 1, hsn_code: '', description: '' }]);
  const [availableItems, setAvailableItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [status] = useState('Unpaid');
  const [showPreview, setShowPreview] = useState(false);
  const [termsAndConditions, setTermsAndConditions] = useState(['Payment is to be cleared within the mentioned time-period. ']);
  const [showHsnColumn, setShowHsnColumn] = useState(false);
  const [taxType, setTaxType] = useState('NONE');
  const [gstRate, setGstRate] = useState(18);
  const [igstRate, setIgstRate] = useState(18);
  const [discount, setDiscount] = useState(0);
  const [hasdiscount, setHasdiscount] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringInterval, setRecurringInterval] = useState('monthly');
  const [recurringEnd, setRecurringEnd] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [pdfKey, setPdfKey] = useState(0);
  const [qrCodeDataURL, setQrCodeDataURL] = useState('');
  const [selectedBankId, setSelectedBankId] = useState('');
  
  // Subscription hook
  const { canCreateInvoice, incrementInvoiceCount, getRemainingInvoices, isSubscribed } = useSubscription();
  
  // Initialize toast hook
  const { showError, showSuccess, showWarning, showInfo, ToastContainer } = useToast();

  const sgstRate = useMemo(() => gstRate / 2, [gstRate]);
  const cgstRate = useMemo(() => gstRate / 2, [gstRate]);

  const checkMobile = useCallback(() => {
    setIsMobile(window.innerWidth < 801);
  }, []);

  // Calculate item total and tax functions
  const getItemTotal = useCallback((item) => {
    const qty = typeof item.quantity === 'string' ? parseInt(item.quantity) || 0 : item.quantity;
    return qty * item.unit_price;
  }, []);

  const calculateItemTax = useCallback((item) => {
    const qty = typeof item.quantity === 'string' ? parseInt(item.quantity) || 0 : item.quantity;
    const base = qty * item.unit_price;
    if (taxType === 'SGST_CGST') {
      return base * ((sgstRate + cgstRate) / 100);
    } else if (taxType === 'IGST') {
      return base * (igstRate / 100);
    }
    return 0;
  }, [taxType, sgstRate, cgstRate, igstRate]);

 

const calculateSummary = useMemo(() => {
  let subtotal = 0;
  
  // Calculate subtotal (sum of all item prices * quantities)
  items.forEach(item => {
    if (!item.product_name || !item.unit_price) return;
    const qty = typeof item.quantity === 'string' ? parseInt(item.quantity) || 0 : item.quantity;
    const base = qty * item.unit_price;
    subtotal += base;
  });
  
  // Calculate discount amount on subtotal
  let discountAmount = 0;
  if (hasdiscount && discount > 0) {
    discountAmount = subtotal * (discount / 100);
  }
  
  // Calculate taxable value (subtotal - discount)
  const taxableValue = subtotal - discountAmount;
  
  // Calculate tax on taxable value (NOT on original subtotal)
  let sgstTotal = 0, cgstTotal = 0, igstTotal = 0, totalTax = 0;
  
  if (taxType === 'SGST_CGST') {
    sgstTotal = taxableValue * (sgstRate / 100);
    cgstTotal = taxableValue * (cgstRate / 100);
    totalTax = sgstTotal + cgstTotal;
  } else if (taxType === 'IGST') {
    igstTotal = taxableValue * (igstRate / 100);
    totalTax = igstTotal;
  }
  
  // Calculate final total (taxable value + tax)
  const total = taxableValue + totalTax;
  
  return { 
    subtotal, 
    totalTax, 
    total, 
    sgstTotal, 
    cgstTotal, 
    igstTotal, 
    Discount: discountAmount, 
    discountAmount: discountAmount,
    taxableValue: taxableValue
  };
}, [items, taxType, sgstRate, cgstRate, igstRate, hasdiscount, discount]);
  useEffect(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [checkMobile]);

  useEffect(() => {
    if (showPreview) {
      const timer = setTimeout(() => {
        setPdfKey(prev => prev + 1);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [items, showPreview]);

  useEffect(() => {
    const fetchUserAndData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setUser(user);
        if (!user) {
          showError('Authentication Error', 'Please log in to create invoices');
          return;
        }
        const [
          { data: userProfile },
          { data: clientList },
          { data: productTemplates },
          { data: bankList }
        ] = await Promise.all([
          supabase.from('users').select('*').eq('id', user.id).single(),
          supabase.from('clients').select('*').eq('user_id', user.id),
          supabase.from('products').select('*').eq('user_id', user.id),
          supabase.from('banks').select('*').eq('user_id', user.id)
        ]);
        setFromInfo(userProfile || {});
        setClients(clientList || []);
        setAvailableItems(productTemplates || []);
        setBanks(bankList || []);
        if (bankList && bankList.length > 0) {
          setSelectedBankId(bankList[0].id);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        showError('Load Error', 'Failed to load invoice data');
      }
    };
    fetchUserAndData();
  }, [showError]);

  const selectedClient = selectedClientId ? clients.find(c => c.id === selectedClientId) : null;
  const selectedBank = selectedBankId ? banks.find(b => b.id === selectedBankId) : null;

  useEffect(() => {
    async function generateQr() {
      if (selectedBank?.upi_id) {
        const upiString = `upi://pay?pa=${selectedBank.upi_id}&am=${calculateSummary.total.toFixed(2)}&pn=${encodeURIComponent(fromInfo?.company_name || fromInfo?.name || 'Merchant')}&tn=${encodeURIComponent(`Invoice ${invoiceInfo.number}`)}&cu=INR`;
        try {
          const qrData = await QRCode.toDataURL(upiString, { width: 150 });
          setQrCodeDataURL(qrData);
        } catch (err) {
          console.error('Error generating QR code:', err);
          setQrCodeDataURL('');
        }
      }
    }
    generateQr();
  }, [selectedBank, calculateSummary.total, fromInfo, invoiceInfo.number]);

  const handleClientSelect = useCallback((clientId) => {
    setSelectedClientId(clientId);
    if (clientId) {
      const selectedClient = clients.find(c => c.id === clientId);
      if (selectedClient) {
        setNewClient({
          name: selectedClient.client_name || '',
          email: selectedClient.client_email || '',
          company: selectedClient.client_company_name || '',
          gst: selectedClient.client_gst_number || '',
          address: selectedClient.address || ''
        });
      }
    } else {
      setNewClient({ name: '', email: '', company: '', gst: '', address: '' });
    }
  }, [clients]);

  const handleAddClientFromPopup = useCallback(() => {
    setShowAddClientPopup(false);
    setSelectedClientId('');
  }, []);

  const handleCancelAddClient = useCallback(() => {
    setShowAddClientPopup(false);
    setNewClient({ name: '', email: '', company: '', gst: '', address: '' });
  }, []);

  const handleProductSelect = useCallback((index, productId) => {
    setItems(prevItems => {
      const updated = [...prevItems];
      if (productId) {
        const product = availableItems.find(p => p.product_id === parseInt(productId));
        if (product) {
          updated[index] = {
            ...updated[index],
            product_id: productId,
            product_name: product.product_name,
            unit_price: product.price_selling,
            hsn_code: product.hsn_code,
            description: product.description,
          };
        }
      } else {
        updated[index] = {
          ...updated[index],
          product_id: '',
          product_name: '',
          unit_price: 0,
          hsn_code: '',
          description: ''
        };
      }
      return updated;
    });
  }, [availableItems]);

  const handleItemChange = useCallback((index, field, value) => {
    setItems(prevItems => {
      const updated = [...prevItems];
      updated[index][field] = value;
      return updated;
    });
  }, []);

  const addItem = useCallback(() => {
    setItems(prevItems => [...prevItems, { product_id: '', product_name: '', unit_price: 0, quantity: 1, hsn_code: '', description: '' }]);
  }, []);

  const removeItem = useCallback((index) => {
    setItems(prevItems => prevItems.filter((_, i) => i !== index));
  }, []);

  const addTerm = useCallback(() => {
    setTermsAndConditions(prev => [...prev, '']);
  }, []);

  const removeTerm = useCallback((index) => {
    setTermsAndConditions(prev => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  }, []);

  const handleTermChange = useCallback((index, value) => {
    setTermsAndConditions(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  }, []);

  const validateInvoice = useCallback(() => {
    if (!invoiceInfo.number || !invoiceInfo.number.trim()) {
      showError('Validation Error', 'Please enter an invoice number');
      return false;
    }

    if (!selectedClientId && !newClient.name.trim()) {
      showError('Validation Error', 'Please select a client or enter client name');
      return false;
    }

    if (newClient.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newClient.email)) {
      showError('Validation Error', 'Please enter a valid email address');
      return false;
    }

    const validItems = items.filter(item => item.product_name && item.unit_price > 0);
    if (validItems.length === 0) {
      showError('Validation Error', 'Please add at least one item with valid details');
      return false;
    }

    if (!invoiceInfo.issueDate) {
      showError('Validation Error', 'Please enter invoice issue date');
      return false;
    }

    if (isRecurring && !recurringEnd) {
      showWarning('Recurring Invoice', 'Recurring end date is not set. Invoice will recur indefinitely.');
    }
    
    if (!invoiceInfo.dueDate) {
      showError('Validation Error', 'Please enter invoice due date');
      return false;
    }

    return true;
  }, [invoiceInfo, selectedClientId, newClient, items, isRecurring, recurringEnd, showError, showWarning]);

const handleSave = useCallback(async (statusOverride, emailSent) => {
    if (!user) {
      showError('Authentication Error', 'User not authenticated');
      return null;
    }

    if (!validateInvoice()) {
      return null;
    }

    try {
      let clientId = selectedClientId;
      if (!clientId && newClient.name) {
        const { data: insertedClient, error } = await supabase.from('clients').insert([{
          user_id: user.id,
          client_name: newClient.name,
          client_email: newClient.email,
          client_company_name: newClient.company,
          client_gst_number: newClient.gst,
          address: newClient.address
        }]).select().single();
        
        if (error) {
          showError('Client Error', error.message || 'Failed to add client');
          return null;
        }
        clientId = insertedClient.id;
      }

      const { subtotal, totalTax, total, discountAmount } = calculateSummary;
      const { data: invoice, error: invoiceError } = await supabase.from('invoices').insert([{
        user_id: user.id,
        client_id: clientId,
        bank_id: selectedBankId || null,
        invoice_number: invoiceInfo.number,
        issue_date: invoiceInfo.issueDate,
        due_date: invoiceInfo.dueDate,
        notes: invoiceInfo.notes,
        termsandconditions: termsAndConditions.filter(term => term.trim() !== '').join('|'),
        
        subtotal,
        total_tax: totalTax,
        total_amount: total,
        is_recurring: isRecurring,
        recurring_interval: isRecurring ? recurringInterval : null,
        recurring_end: isRecurring && recurringEnd ? recurringEnd : null,
        last_created_date: isRecurring ? invoiceInfo.issueDate : null,
        discount: hasdiscount ? discount : 0, // ✅ FIXED: Save percentage, not amount
        email_sent: emailSent,
        status: statusOverride
      }]).select().single();
      
      if (invoiceError) {
        showError('Invoice Error', invoiceError.message || 'Failed to create invoice');
        return null;
      }

      const itemPayload = items
        .filter(item => item.product_name && item.unit_price > 0)
        .map(item => {
          const qty = typeof item.quantity === 'string' ? parseInt(item.quantity) || 0 : item.quantity;
          const [sgstPercent, cgstPercent] = taxType === 'SGST_CGST' 
            ? [sgstRate, cgstRate] 
            : taxType === 'IGST' 
            ? [igstRate, 0] 
            : [0, 0];
          return {
            invoice_id: invoice.id,
            name: item.product_name,
            description: item.description || '',
            hsn_sac: item.hsn_code || '',
            quantity: qty,
            rate_per_item: item.unit_price,
            sgst_percent: sgstPercent,
            cgst_percent: cgstPercent
          };
        });
      
      const { error: itemsError } = await supabase.from('invoice_items').insert(itemPayload);
      if (itemsError) {
        console.error('Error inserting invoice items:', itemsError);
        showWarning('Items Warning', 'Invoice created but some items may not have been saved');
      }

      // ✅ INCREMENT INVOICE COUNT AFTER SUCCESSFUL CREATION
      await incrementInvoiceCount();

      return invoice.id;
    } catch (error) {
      console.error('Error saving invoice:', error);
      showError('Save Error', 'An unexpected error occurred while saving');
      return null;
    }
  }, [user, invoiceInfo, selectedClientId, newClient, calculateSummary, selectedBankId, termsAndConditions, isRecurring, recurringInterval, recurringEnd, items, taxType, sgstRate, cgstRate, igstRate, hasdiscount, discount, validateInvoice, incrementInvoiceCount, showError, showWarning]);

  const handleSaveOnly = useCallback(async () => {
    try {
      const invoiceId = await handleSave('Unpaid', false);
      if (invoiceId) {
        showSuccess('Success', `Invoice #${invoiceInfo.number} saved successfully!`);
        setShowPreview(false);
      }
    } catch (error) {
      console.error('Error saving invoice:', error);
      showError('Save Error', 'Failed to save invoice');
    }
  }, [handleSave, invoiceInfo.number, showSuccess, showError]);

  const handleBack = useCallback(() => {
    setShowPreview(false);
    setPdfError(null);
  }, []);

  // Helper functions defined before handleSend
  const numberToWords = useCallback((num) => {
    if (!num || num === 0) return 'Zero only';
    return `${Math.round(num)} only`;
  }, []);

  const getLocalItemTotal = useCallback((item) => {
    const qty = typeof item.quantity === 'string' ? parseInt(item.quantity) || 0 : item.quantity;
    return qty * (item.unit_price || item.rate_per_item || 0);
  }, []);

  const calculateLocalItemTax = useCallback((item) => {
    return calculateItemTax(item);
  }, [calculateItemTax]);

  const memoizedInvoiceData = useMemo(() => {
    const safeFromInfo = fromInfo || {
      company_name: 'Your Company',
      name: 'Your Name',
      address: '',
      phone: '',
      email: '',
      gst_number: ''
    };
    const safeNewClient = newClient || {
      name: 'Customer Name',
      email: '',
      company: '',
      gst: '',
      address: ''
    };
    const safeInvoiceInfo = invoiceInfo || {
      number: 'INV-001',
      issueDate: new Date().toISOString().split('T')[0],
      dueDate: '',
      notes: ''
    };
    const safeTermsAndConditions = termsAndConditions.length > 0 ? termsAndConditions : ['Payment due within 30 days'];
    return {
      fromInfo: safeFromInfo,
      selectedClient,
      newClient: safeNewClient,
      invoiceInfo: safeInvoiceInfo,
      termsAndConditions: safeTermsAndConditions,
      userProfile: safeFromInfo
    };
  }, [fromInfo, selectedClient, newClient, invoiceInfo, termsAndConditions]);

  const memoizedNumberToWords = useCallback((num) => {
    if (!num || num === 0) return 'ZERO RUPEES ONLY';
    
    const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE'];
    const teens = ['TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
    const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

    const convertHundreds = (n) => {
      let result = '';
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' HUNDRED ';
        n %= 100;
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        result += teens[n - 10] + ' ';
        n = 0;
      }
      if (n > 0) {
        result += ones[n] + ' ';
      }
      return result;
    };

    const convertLargeNumber = (n) => {
      if (n === 0) return '';
      let result = '';
      let crores = Math.floor(n / 10000000);
      n %= 10000000;
      let lakhs = Math.floor(n / 100000);
      n %= 100000;
      let thousands = Math.floor(n / 1000);
      n %= 1000;

      if (crores > 0) result += convertHundreds(crores) + 'CRORE ';
      if (lakhs > 0) result += convertHundreds(lakhs) + 'LAKH ';
      if (thousands > 0) result += convertHundreds(thousands) + 'THOUSAND ';
      if (n > 0) result += convertHundreds(n);

      return result;
    };

    const integerPart = Math.floor(num);
    const decimalPart = Math.round((num - integerPart) * 100);
    let result = integerPart === 0 ? 'ZERO' : convertLargeNumber(integerPart).trim();

    result += ' RUPEES';
    if (decimalPart > 0) {
      result += ' AND ' + convertHundreds(decimalPart).trim() + ' PAISE';
    }
    result += ' ONLY';
    return result;
  }, []);

  const handleSend = useCallback(async () => {
    try {
      showInfo('Processing', 'Saving invoice and preparing email...');
      const invoiceId = await handleSave('Unpaid', true);
      if (!invoiceId) return;

      // Fetch the saved invoice and related data
      const [invoiceResult, itemsResult, clientResult, bankResult, userResult] = await Promise.all([
        supabase.from('invoices').select('*').eq('id', invoiceId).single(),
        supabase.from('invoice_items').select('*').eq('invoice_id', invoiceId).order('id'),
        supabase.from('clients').select('*').eq('id', selectedClientId).single(),
        supabase.from('banks').select('*').eq('user_id', user.id),
        supabase.from('users').select('*').eq('id', user.id).single()
      ]);

      const invData = invoiceResult.data;
      const itemsData = itemsResult.data || [];
      const clientData = clientResult.data;
      const bankData = bankResult.data || [];
      const userProfileData = userResult.data;

      if (!clientData?.client_email && !clientData?.email) {
        showError('Email Error', 'Client email not found. Please add client email and try again.');
        return;
      }

      showInfo('Generating PDF', 'Creating invoice PDF...');

      // Prepare invoice data for PDF
      const invoiceData = {
        fromInfo: {
          company_name: userProfileData?.company_name || fromInfo?.company_name || 'Your Company',
          name: userProfileData?.full_name || fromInfo?.name || 'Your Name',
          address: userProfileData?.address || fromInfo?.address || '',
          phone: userProfileData?.phone || fromInfo?.phone || '',
          email: userProfileData?.email || user?.email || '',
          contact_email: userProfileData?.email || user?.email || '',
          gst_number: userProfileData?.gst_number || fromInfo?.gst_number || ''
        },
        selectedClient: {
          client_name: clientData?.client_name || newClient.name || '',
          client_company_name: clientData?.client_company_name || newClient.company || '',
          address: clientData?.address || newClient.address || '',
          email: clientData?.client_email || clientData?.email || newClient.email || '',
          client_email: clientData?.client_email || clientData?.email || newClient.email || '',
          client_gst_number: clientData?.client_gst_number || newClient.gst || ''
        },
        invoiceInfo: {
          number: invData?.invoice_number || invoiceInfo.number,
          issueDate: invData?.issue_date || invoiceInfo.issueDate,
          dueDate: invData?.due_date || invoiceInfo.dueDate,
          notes: invData?.notes || invoiceInfo.notes
        },
        termsAndConditions: termsAndConditions.filter(term => term.trim() !== ''),
        userProfile: userProfileData
      };

      const selectedBankForPdf = bankData && bankData.length > 0 ? bankData[0] : null;

      // Prepare items for PDF from database
      const pdfItems = itemsData.map((item, index) => ({
        product_name: item.name || '',
        description: item.description || '',
        hsn_code: item.hsn_sac || '',
        quantity: item.quantity || 0,
        unit_price: item.rate_per_item || 0,
        stableKey: `item-${index}`
      }));

      // Generate PDF using InvoicePDF component
      const { pdf } = await import('@react-pdf/renderer');
      const doc = (
        <InvoicePDF
          invoiceData={invoiceData}
          items={pdfItems}
          localSummary={calculateSummary}
          numberToWords={memoizedNumberToWords}
          getLocalItemTotal={getLocalItemTotal}
          calculateLocalItemTax={calculateLocalItemTax}
          showTaxPerItemColumn={taxType !== 'NONE'}
          showHsnColumn={showHsnColumn}
          hasTax={taxType !== 'NONE'}
          hasdiscount={hasdiscount}
          discount={discount}
          taxType={taxType}
          sgstRate={sgstRate}
          cgstRate={cgstRate}
          igstRate={igstRate}
          selectedBank={selectedBankForPdf}
          qrCodeDataURL={qrCodeDataURL}
          showValueColumn={taxType !== 'NONE'}
        />
      );

      const pdfBlob = await pdf(doc).toBlob();

      // Convert PDF to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });

      showInfo('Sending Email', `Sending invoice to ${clientData.client_email || clientData.email}...`);

      // Send email with PDF attachment
      const res = await fetch(
        'https://dzwtnmlmjwbwqevbhwnv.supabase.co/functions/v1/send-invoice-email',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            from: 'no-reply@bossbyko.com',
            to: clientData.client_email || clientData.email,
            subject: `Invoice ${invoiceInfo.number}`,
            html: `<p>Hello ${clientData.client_name || newClient.name},</p><p>Please find attached invoice <strong>${invoiceInfo.number}</strong>.</p><p>Best regards,<br>${invoiceData.fromInfo.company_name || invoiceData.fromInfo.name}</p>`,
            attachments: [{
              filename: `invoice-${invoiceInfo.number}.pdf`,
              content: base64,
              type: 'application/pdf'
            }]
          })
        }
      );

      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Failed to send email');

      // Update invoice as sent
      const { error: updateErr } = await supabase
        .from('invoices')
        .update({ email_sent: true })
        .eq('id', invoiceId)
        .eq('user_id', user.id);

      if (updateErr) throw updateErr;

      showSuccess('Success', `Invoice #${invoiceInfo.number} saved and sent successfully to ${clientData.client_email || clientData.email}!`);
      setShowPreview(false);
    } catch (error) {
      console.error('Error sending invoice:', error);
      showError('Send Error', error.message || 'Failed to send invoice email');
    }
  }, [handleSave, selectedClientId, user, newClient, fromInfo, invoiceInfo, termsAndConditions, calculateSummary, memoizedNumberToWords, getLocalItemTotal, calculateLocalItemTax, taxType, showHsnColumn, hasdiscount, discount, sgstRate, cgstRate, igstRate, qrCodeDataURL, showInfo, showSuccess, showError]);

  const memoizedSafeItems = useMemo(() => {
    const validItems = items.filter(item => item.product_name && item.unit_price);
    return validItems.length > 0 ? validItems.map((item, index) => ({
      ...item,
      stableKey: `item-${item.product_id || index}-${item.product_name || 'empty'}`
    })) : [{
      product_id: '1',
      product_name: 'Sample Product',
      unit_price: 100,
      quantity: 1,
      hsn_code: '1234',
      description: 'Sample description',
      stableKey: 'sample-item-1'
    }];
  }, [items]);

  // Handle preview with subscription check
  const handlePreviewClick = useCallback(() => {
    if (!canCreateInvoice()) {
      showError(
        'Invoice Limit Reached', 
        `You've reached the free plan limit of 2 invoices. Please upgrade to continue creating invoices.`
      );
      return;
    }
    setPdfError(null);
    setShowPreview(true);
  }, [canCreateInvoice, showError]);

  return (
    <>
      {/* Toast Container */}
      <ToastContainer />

      <div className="InvoiceForm__container">
        <h1 className="InvoiceForm__title">Create Invoice</h1>
        
        {/* Subscription Alert Banner */}
        {!isSubscribed && (
          <div className="invoice-form-subscription-alert" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 16px',
            backgroundColor: '#FEF3C7',
            border: '1px solid #F59E0B',
            borderRadius: '8px',
            marginBottom: '20px',
            color: '#92400E'
          }}>
            <AlertCircle size={20} />
            <span>
              Free Plan: {getRemainingInvoices()} of 2 invoices remaining
            </span>
          </div>
        )}
        
        <section className="InvoiceForm__details-format-wrapper">
          <div className="InvoiceForm__details-box">
            <div className="InvoiceForm__box-content">
              <h2 className="InvoiceForm__section-title">Client & Invoice Details</h2>
              <div className="InvoiceForm__horizontal-sections">
                <ClientSelection
                  clients={clients}
                  selectedClientId={selectedClientId}
                  newClient={newClient}
                  onClientSelect={handleClientSelect}
                  onShowAddClient={() => setShowAddClientPopup(true)}
                />
                <InvoiceDetails
                  invoiceInfo={invoiceInfo}
                  setInvoiceInfo={setInvoiceInfo}
                />
              </div>
            </div>
          </div>
          <div className="InvoiceForm__format-box">
            <div className="InvoiceForm__box-content">
              <h2 className="InvoiceForm__section-title">Invoice Format</h2>
              <InvoiceOptions
                showHsnColumn={showHsnColumn}
                setShowHsnColumn={setShowHsnColumn}
                taxType={taxType}
                setTaxType={setTaxType}
                gstRate={gstRate}
                setGstRate={setGstRate}
                igstRate={igstRate}
                setIgstRate={setIgstRate}
                hasdiscount={hasdiscount}
                setHasdiscount={setHasdiscount}
                discount={discount}
                setDiscount={setDiscount}
                isMobile={isMobile}
              />
            </div>
          </div>
        </section>
        
        {showAddClientPopup && (
          <AddClientPopup
            newClient={newClient}
            setNewClient={setNewClient}
            onSave={handleAddClientFromPopup}
            onCancel={handleCancelAddClient}
          />
        )}
        
        <ItemsSection
          items={items}
          availableItems={availableItems}
          showHsnColumn={showHsnColumn}
          taxType={taxType}
          hasdiscount={hasdiscount}
          isMobile={isMobile}
          onProductSelect={handleProductSelect}
          onItemChange={handleItemChange}
          onAddItem={addItem}
          onRemoveItem={removeItem}
          getItemTotal={getItemTotal}
          calculateItemTax={calculateItemTax}
        />
        
        <section className="InvoiceForm__terms-summary-container">
          <div className="InvoiceForm__terms-summary-wrapper">
            <div className="InvoiceForm__terms-box">
              <div className="InvoiceForm__box-content">
                <TermsConditions
                  termsAndConditions={termsAndConditions}
                  onTermChange={handleTermChange}
                  onAddTerm={addTerm}
                  onRemoveTerm={removeTerm}
                  isMobile={isMobile}
                />
              </div>
            </div>
            <div className="InvoiceForm__summary-box">
              <div className="InvoiceForm__box-content">
                <InvoiceSummary
                  calculateSummary={calculateSummary}
                  taxType={taxType}
                  sgstRate={sgstRate}
                  cgstRate={cgstRate}
                  igstRate={igstRate}
                  hasdiscount={hasdiscount}
                  discount={discount}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="InvoiceForm__section">
          <RecurringInvoiceSection
            isRecurring={isRecurring}
            setIsRecurring={setIsRecurring}
            recurringInterval={recurringInterval}
            setRecurringInterval={setRecurringInterval}
            recurringEnd={recurringEnd}
            setRecurringEnd={setRecurringEnd}
            isMobile={isMobile}
          />
        </section>
        
        <button 
          onClick={handlePreviewClick}
          className="InvoiceForm__button InvoiceForm__button--preview"
          disabled={!canCreateInvoice()}
          style={{
            opacity: !canCreateInvoice() ? 0.6 : 1,
            cursor: !canCreateInvoice() ? 'not-allowed' : 'pointer'
          }}
        >
          {canCreateInvoice() ? 'Preview Invoice' : 'Upgrade to Create More Invoices'}
        </button>
        
        {showPreview && (
          <div className="InvoiceForm__pdf-popup-overlay" key={`pdf-overlay-${pdfKey}`}>
            <div className="InvoiceForm__pdf-popup">
              {pdfError ? (
                <div className="InvoiceForm__pdf-error">
                  <h3>PDF Error:</h3>
                  <p>{pdfError}</p>
                  <button onClick={() => {
                    setPdfError(null);
                    setPdfKey(prev => prev + 1);
                  }}>Retry</button>
                </div>
              ) : (
                <PDFViewer 
                  width="100%" 
                  height="100%" 
                  className="InvoiceForm__pdf-viewer"
                  key={`pdf-viewer-${pdfKey}`}
                  onError={(error) => {
                    console.error('PDF Viewer Error:', error);
                    setPdfError(error.message || 'Failed to render PDF');
                    showError('PDF Error', 'Failed to render PDF preview');
                  }}
                >
                  <InvoicePDF
                    invoiceData={memoizedInvoiceData}
                    items={memoizedSafeItems}
                    localSummary={calculateSummary}
                    numberToWords={memoizedNumberToWords}
                    getLocalItemTotal={getLocalItemTotal}
                    calculateLocalItemTax={calculateLocalItemTax}
                    showTaxPerItemColumn={taxType !== 'NONE'}
                    showHsnColumn={showHsnColumn}
                    hasTax={taxType !== 'NONE'}
                    hasdiscount={hasdiscount}
                    discount={discount}
                    taxType={taxType}
                    sgstRate={sgstRate}
                    cgstRate={cgstRate}
                    igstRate={igstRate}
                    selectedBank={selectedBank}
                    qrCodeDataURL={qrCodeDataURL}
                    showValueColumn={taxType !== 'NONE'}
                  />
                </PDFViewer>
              )}
              <div className="InvoiceForm__pdf-popup-actions">
                <button onClick={handleBack} className="InvoiceForm__button InvoiceForm__button--popup">
                  Back & Edit
                </button>
                <button onClick={handleSaveOnly} className="InvoiceForm__button InvoiceForm__button--popup">
                  Save
                </button>
                <button onClick={handleSend} className="InvoiceForm__button InvoiceForm__button--popup">
                  Send
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
});

InvoiceForm.displayName = 'InvoiceForm';
export default InvoiceForm;
